import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LogOut, Mail, Phone, RefreshCw, Trash2, Search, Building2, Home, Clock,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";

const STATUS = {
  new: { label: "New", cls: "border-cyanbrand/50 bg-cyanbrand/10 text-cyanbrand" },
  contacted: { label: "Contacted", cls: "border-amber-400/50 bg-amber-400/10 text-amber-300" },
  closed: { label: "Closed", cls: "border-white/20 bg-white/5 text-white/50" },
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const { user, logout } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/quotes");
      setQuotes(data);
    } catch {
      toast.error("Could not load quote requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateQuote = async (id, fields) => {
    try {
      const { data } = await api.patch(`/admin/quotes/${id}`, fields);
      setQuotes((prev) => prev.map((x) => (x.id === id ? data : x)));
      toast.success("Updated.");
    } catch {
      toast.error("Update failed.");
    }
  };

  const removeQuote = async (id) => {
    if (!window.confirm("Delete this quote request permanently?")) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      setQuotes((prev) => prev.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const stats = useMemo(() => ({
    total: quotes.length,
    new: quotes.filter((x) => x.status === "new").length,
    contacted: quotes.filter((x) => x.status === "contacted").length,
    closed: quotes.filter((x) => x.status === "closed").length,
  }), [quotes]);

  const visible = useMemo(() => {
    return quotes.filter((x) => {
      if (filter !== "all" && x.status !== filter) return false;
      if (!q) return true;
      const s = `${x.name} ${x.email} ${x.phone} ${x.service}`.toLowerCase();
      return s.includes(q.toLowerCase());
    });
  }, [quotes, filter, q]);

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8 h-16">
          <div className="flex items-center gap-2.5">
            <LogoMark className="h-8 w-8" />
            <span className="font-display text-base font-semibold tracking-tight">
              SC<span className="text-cyanbrand">·</span>Security
              <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Back office</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden font-body text-sm text-white/50 sm:inline">{user?.email}</span>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-white/80 transition-colors hover:border-redbrand hover:text-redbrand"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight">Quote requests</h1>
            <p className="mt-2 font-body text-sm text-white/50">Review enquiries and track your response.</p>
          </div>
          <button
            onClick={load}
            data-testid="refresh-btn"
            className="inline-flex items-center gap-2 self-start rounded-full border border-white/15 px-4 py-2 text-sm text-white/70 transition-colors hover:border-cyanbrand hover:text-cyanbrand md:self-auto"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            { k: "total", label: "Total", val: stats.total },
            { k: "new", label: "New", val: stats.new },
            { k: "contacted", label: "Contacted", val: stats.contacted },
            { k: "closed", label: "Closed", val: stats.closed },
          ].map((s) => (
            <div key={s.k} data-testid={`stat-${s.k}`} className="border border-white/10 bg-[#0c0c0c] p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{s.label}</p>
              <p className="mt-2 font-display text-3xl font-semibold text-cyanbrand">{s.val}</p>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {["all", "new", "contacted", "closed"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
                className={`rounded-full border px-4 py-2 text-sm capitalize transition-colors ${
                  filter === f ? "border-cyanbrand bg-cyanbrand/10 text-cyanbrand" : "border-white/15 text-white/60 hover:border-white/40"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="search-input"
              placeholder="Search name, email, service…"
              className="h-11 w-full border border-white/15 bg-black pl-10 pr-3 text-sm text-white placeholder:text-white/30 focus:border-cyanbrand focus:outline-none"
            />
          </div>
        </div>

        {/* List */}
        <div className="mt-6 space-y-4">
          {loading && <p className="py-16 text-center font-mono text-sm text-white/40">Loading…</p>}
          {!loading && visible.length === 0 && (
            <p data-testid="empty-state" className="border border-white/10 bg-[#0c0c0c] py-16 text-center font-mono text-sm text-white/40">
              No quote requests found.
            </p>
          )}
          {!loading && visible.map((quote, i) => (
            <QuoteCard key={quote.id} quote={quote} i={i} onUpdate={updateQuote} onDelete={removeQuote} />
          ))}
        </div>
      </div>
    </main>
  );
}

function QuoteCard({ quote, i, onUpdate, onDelete }) {
  const [notes, setNotes] = useState(quote.notes || "");
  const st = STATUS[quote.status] || STATUS.new;
  const PropIcon = quote.property_type === "commercial" ? Building2 : Home;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(i * 0.03, 0.3) }}
      data-testid={`quote-card-${quote.id}`}
      className="border border-white/10 bg-[#0c0c0c] p-6 transition-colors hover:border-white/20"
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="font-display text-xl font-medium tracking-tight">{quote.name}</h3>
            <span className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${st.cls}`}>
              {st.label}
            </span>
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-white/40">
              <PropIcon className="h-3.5 w-3.5" /> {quote.property_type}
            </span>
          </div>

          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 font-body text-sm">
            <a href={`mailto:${quote.email}`} className="inline-flex items-center gap-2 text-white/70 hover:text-cyanbrand">
              <Mail className="h-4 w-4" /> {quote.email}
            </a>
            <a href={`tel:${quote.phone}`} className="inline-flex items-center gap-2 text-white/70 hover:text-cyanbrand">
              <Phone className="h-4 w-4" /> {quote.phone}
            </a>
            <span className="inline-flex items-center gap-2 text-white/40">
              <Clock className="h-4 w-4" /> {fmtDate(quote.created_at)}
            </span>
          </div>

          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-cyanbrand">{quote.service}</p>
          {quote.message && (
            <p className="mt-2 max-w-2xl border-l-2 border-white/10 pl-4 font-body text-sm text-white/60 leading-relaxed">
              {quote.message}
            </p>
          )}

          <div className="mt-4">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid={`notes-${quote.id}`}
              placeholder="Internal notes…"
              rows={2}
              className="w-full max-w-2xl border border-white/10 bg-black px-3 py-2 font-body text-sm text-white placeholder:text-white/25 focus:border-cyanbrand focus:outline-none"
            />
            {notes !== (quote.notes || "") && (
              <button
                onClick={() => onUpdate(quote.id, { notes })}
                data-testid={`save-notes-${quote.id}`}
                className="mt-2 rounded-full bg-white/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-white hover:bg-white/20"
              >
                Save notes
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex shrink-0 flex-col gap-2 lg:w-52">
          <a
            href={`mailto:${quote.email}?subject=Your SC Security quote request&body=Hi ${encodeURIComponent(quote.name)},%0D%0A%0D%0AThank you for your enquiry about ${encodeURIComponent(quote.service)}.`}
            data-testid={`reply-${quote.id}`}
            className="inline-flex items-center justify-center gap-2 bg-cyanbrand py-2.5 text-sm font-semibold text-black transition-transform hover:scale-[1.02]"
          >
            <Mail className="h-4 w-4" /> Reply
          </a>
          <div className="grid grid-cols-3 gap-1.5">
            {["new", "contacted", "closed"].map((s) => (
              <button
                key={s}
                onClick={() => onUpdate(quote.id, { status: s })}
                data-testid={`set-${s}-${quote.id}`}
                className={`border py-2 font-mono text-[9px] uppercase tracking-wider transition-colors ${
                  quote.status === s ? "border-cyanbrand text-cyanbrand" : "border-white/15 text-white/50 hover:border-white/40"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <button
            onClick={() => onDelete(quote.id)}
            data-testid={`delete-${quote.id}`}
            className="inline-flex items-center justify-center gap-2 border border-white/10 py-2 text-sm text-white/40 transition-colors hover:border-redbrand hover:text-redbrand"
          >
            <Trash2 className="h-4 w-4" /> Delete
          </button>
        </div>
      </div>
    </motion.div>
  );
}
